import type { UploadStatus } from '../upload/transfer/upload-file';

/**
 * The copy of the upload & processing screen (`ProcessingStage`) — keys of
 * the shared `processing.*` bundle (`shared/src/locales/<lang>/<lang>-processing.json`),
 * translated by each app. One vocabulary on both clients, so one wait never
 * reads two ways.
 *
 * Title rule: sentence case, names what is being worked on ("your document"),
 * ends with ONE "…" that the copy carries; no emoji, no "Ka is". Pinned by
 * `processing.spec.ts` over every locale.
 */

export const PROCESSING_TITLE_KEYS = {
  uploadingFile: 'processing.title.uploadingFile',
  uploadingDocument: 'processing.title.uploadingDocument',
  uploadingRecording: 'processing.title.uploadingRecording',
  readingDocument: 'processing.title.readingDocument',
  openingDocument: 'processing.title.openingDocument',
  creatingDeck: 'processing.title.creatingDeck',
  addingToDeck: 'processing.title.addingToDeck',
  transcribingRecording: 'processing.title.transcribingRecording',
  transcribingVideo: 'processing.title.transcribingVideo',
  fetchingTranscript: 'processing.title.fetchingTranscript',
  writingNotes: 'processing.title.writingNotes',
  uploadingAnki: 'processing.title.uploadingAnki',
  readingAnki: 'processing.title.readingAnki',
  retrying: 'processing.title.retrying',
  waitingForNetwork: 'processing.title.waitingForNetwork',
  /** Image occlusion's scan for words on a picture (no stage of its own). */
  findingText: 'processing.title.findingText',
  writingQuestions: 'processing.title.writingQuestions',
  buildingCase: 'processing.title.buildingCase',
  planningExam: 'processing.title.planningExam',
} as const;

export type ProcessingTitleKey =
  (typeof PROCESSING_TITLE_KEYS)[keyof typeof PROCESSING_TITLE_KEYS];

/** One-line explanations under a title, where the wait needs one. */
export const PROCESSING_DETAIL_KEYS = {
  longRecording: 'processing.detail.longRecording',
  longVideo: 'processing.detail.longVideo',
  /** A live recording waiting for the network: nothing is lost by waiting. */
  recordingSafeOnDevice: 'processing.detail.recordingSafeOnDevice',
  /** What building a case study involves (it replaces the pro tip there). */
  buildingCase: 'processing.detail.buildingCase',
} as const;

export const PROCESSING_PRO_TIP_LABEL_KEY = 'processing.proTip';

/** The pro tips the screen rotates through, in order. */
export const PROCESSING_TIP_KEYS = [
  'processing.tips.highlightMissing',
  'processing.tips.studySpacedRepetition',
  'processing.tips.reinforceWithQuizzes',
  'processing.tips.strugglingRecall',
  'processing.tips.testYourself',
  'processing.tips.typeToAnswer',
  'processing.tips.addImagesEmojis',
  'processing.tips.breakDownConcepts',
  'processing.tips.customiseDecks',
] as const;

export type ProcessingTipKey = (typeof PROCESSING_TIP_KEYS)[number];

/** How long each pro tip stays before the next one. */
export const PROCESSING_TIP_INTERVAL_MS = 5000;

/**
 * The title of an upload wait: what the transfer is doing when it is not
 * simply moving bytes (a retry, no network), else the caller's own
 * "Uploading your …" title.
 */
export function uploadStageTitleKey(
  status: UploadStatus | null | undefined,
  uploadingKey: ProcessingTitleKey = PROCESSING_TITLE_KEYS.uploadingFile,
): ProcessingTitleKey {
  switch (status?.phase) {
    case 'retrying':
      return PROCESSING_TITLE_KEYS.retrying;
    case 'waiting-for-network':
      return PROCESSING_TITLE_KEYS.waitingForNetwork;
    default:
      return uploadingKey;
  }
}
